import React from 'react';
import Label from '../atoms/Label';
import DatePicker from '../atoms/DatePicker';
import { cn } from '../utils/cn';

/**
 * FormDatePicker Component - Molecule
 * Combines Label and DatePicker atoms
 * 
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.name - Input name
 * @param {Date|string|null} props.value - Selected date
 * @param {Function} props.onChange - Change handler
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text
 * @param {boolean} props.required - Required field
 */
const FormDatePicker = React.forwardRef(
  (
    {
      label,
      name,
      value,
      onChange,
      error,
      helperText,
      required = false,
      className = '',
      labelClassName = '',
      ...datePickerProps
    },
    ref
  ) => {
    const handleChange = (date) => {
      if (onChange) {
        // Check if date is valid before processing
        if (date && date instanceof Date && !isNaN(date.getTime())) {
          // Format date as YYYY-MM-DD in local timezone to avoid timezone issues
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dateString = `${year}-${month}-${day}`;

          const event = {
            target: {
              name,
              value: dateString,
            },
          };
          onChange(event);
        } else if (!date) {
          // Handle null/undefined date
          const event = {
            target: {
              name,
              value: '',
            },
          };
          onChange(event);
        }
      }
    };

    // Convert string value to Date if needed
    // Handle date strings as local dates to avoid timezone issues
    const dateValue = (() => {
      if (!value) return null;
      // Robust Date check
      if (value && typeof value.getTime === 'function') {
        // Check if date is valid
        return isNaN(value.getTime()) ? null : value;
      }
      if (typeof value === 'string' && value !== '') {
        // Parse date string as local date (YYYY-MM-DD format)
        // This prevents timezone shifts
        const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch) {
          const year = parseInt(dateMatch[1], 10);
          const month = parseInt(dateMatch[2], 10) - 1; // Month is 0-indexed
          const day = parseInt(dateMatch[3], 10);
          const date = new Date(year, month, day);
          return isNaN(date.getTime()) ? null : date;
        }
        // Fallback to standard Date parsing
        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
      }
      return null;
    })();

    return (
      <div className={cn('space-y-2', className)}>
        {label && (
          <Label
            htmlFor={name}
            required={required}
            className={labelClassName}
          >
            {label}
          </Label>
        )}
        <DatePicker
          ref={ref}
          id={name}
          name={name}
          selected={dateValue}
          onChange={handleChange}
          variant={error ? 'error' : 'default'}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error || helperText
              ? `${name}-${error ? 'error' : 'helper'}`
              : undefined
          }
          {...datePickerProps}
        />
        {error && (
          <p
            id={`${name}-error`}
            className="text-sm text-error-600 dark:text-error-400"
            role="alert"
          >
            {error}
          </p>
        )}
        {helperText && !error && (
          <p
            id={`${name}-helper`}
            className="text-sm text-primary-500 dark:text-primary-400"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

FormDatePicker.displayName = 'FormDatePicker';

export default FormDatePicker;
